import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Copy, MoveHorizontal, Circle, Trash2, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';

export const initialSpacingScale = [
  { name: 'Base', minSize: 6, maxSize: 8 },
  { name: 'Gap', minSize: 12, maxSize: 16 },
  { name: 'Card Padding', minSize: 16, maxSize: 24 },
  { name: 'Section Padding', minSize: 48, maxSize: 80 },
];

export const initialRadiusScale = [
  { name: 'none', minSize: 0, maxSize: 0 },
  { name: 'xs', minSize: 2, maxSize: 4 },
  { name: 'sm', minSize: 4, maxSize: 8 },
  { name: 'md', minSize: 8, maxSize: 12 },
  { name: 'lg', minSize: 12, maxSize: 16 },
  { name: 'pill', minSize: 9999, maxSize: 9999 },
];

export const initialElevationScale = [
  { name: 'Level 0 — Flat', reference: 'border + hairline', value: '0 0 0 0 transparent', className: 'border border-border' },
  { name: 'Level 1 — Subtle', reference: '', value: '0 1px 2px rgba(5,0,56,0.04)', className: 'border border-border/50 shadow-[0_1px_2px_rgba(5,0,56,0.04)]' },
  { name: 'Level 2 — Card', reference: '', value: '0 4px 12px rgba(5,0,56,0.06)', className: 'border border-border/50 shadow-[0_4px_12px_rgba(5,0,56,0.06)]' },
  { name: 'Level 3 — Ring', reference: 'accessibility border', value: '0 0 0 2px var(--primary)', className: 'border border-primary ring-2 ring-primary' },
];

export function SpacingEditor() {
  const { toast } = useToast();
  const [spacingScale, setSpacingScale] = useState(initialSpacingScale);
  const [radiusScale, setRadiusScale] = useState(initialRadiusScale);
  const [elevationScale, setElevationScale] = useState(initialElevationScale);

  const generateClampValue = (minSize: number, maxSize: number): string => {
    if (minSize === maxSize) return `${minSize}px`;
    const minViewport = 320;
    const maxViewport = 1200;
    const slope = (maxSize - minSize) / (maxViewport - minViewport);
    const yAxisIntersection = -minViewport * slope + minSize;
    return `clamp(${Math.round(minSize)}px, ${Math.round(yAxisIntersection)}px + ${(slope * 100).toFixed(2)}vw, ${Math.round(maxSize)}px)`;
  };

  const handleCopyClampValue = async (clampValue: string) => {
    try {
      await navigator.clipboard.writeText(clampValue);
      toast({
        title: 'Copied!',
        description: 'Value copied to clipboard',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleScaleValueChange = (index: number, field: 'minSize' | 'maxSize', value: string) => {
    const numValue = Math.round(parseFloat(value));
    if (isNaN(numValue) || numValue < 0) return;

    setSpacingScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: numValue };
      return next;
    });
  };

  const handleNameChange = (index: number, value: string) => {
    setSpacingScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], name: value };
      return next;
    });
  };

  const handleRadiusValueChange = (index: number, field: 'minSize' | 'maxSize', value: string) => {
    const numValue = Math.round(parseFloat(value));
    if (isNaN(numValue) || numValue < 0) return;

    setRadiusScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: numValue };
      return next;
    });
  };

  const handleRadiusNameChange = (index: number, value: string) => {
    setRadiusScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], name: value };
      return next;
    });
  };

  const handleAddSpacing = () => {
    setSpacingScale(prev => [...prev, { name: 'New Variable', minSize: 16, maxSize: 16 }]);
  };

  const handleAddRadius = () => {
    setRadiusScale(prev => [...prev, { name: 'New Variable', minSize: 8, maxSize: 8 }]);
  };

  const handleRemoveSpacing = (index: number) => {
    setSpacingScale(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveRadius = (index: number) => {
    setRadiusScale(prev => prev.filter((_, i) => i !== index));
  };

  const handleElevationNameChange = (index: number, value: string) => {
    setElevationScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], name: value };
      return next;
    });
  };

  const handleElevationReferenceChange = (index: number, reference: string) => {
    setElevationScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], reference };
      return next;
    });
  };

  const handleElevationValueChange = (index: number, value: string) => {
    setElevationScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], value: value };
      return next;
    });
  };

  const handleElevationClassChange = (index: number, value: string) => {
    setElevationScale(prev => {
      const next = [...prev];
      next[index] = { ...next[index], className: value };
      return next;
    });
  };

  const handleAddElevation = () => {
    setElevationScale(prev => [...prev, { name: 'New Level', reference: '', value: 'none', className: 'border border-transparent' }]);
  };

  const handleRemoveElevation = (index: number) => {
    setElevationScale(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-y-auto p-6 max-w-2xl mx-auto w-full">
      {/* SPACING */}
      <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-8 shrink-0">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border/50 bg-muted/20">
          <MoveHorizontal className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground uppercase">Spacing</h3>
        </div>
        <div className="p-6 flex flex-col gap-1">
          {spacingScale.map((scale, i) => (
            <Popover key={i}>
              <PopoverTrigger asChild>
                <div className="flex items-center justify-between py-2.5 px-3 -mx-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors group">
                  <div className="w-[120px] text-sm font-medium text-foreground truncate pr-2">{scale.name}</div>
                  <div className="flex-1 px-4 flex items-center">
                    <div className="h-1.5 w-full bg-muted/20 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min((scale.maxSize / 96) * 100, 100)}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-[40px] text-right text-xs text-muted-foreground">{scale.maxSize}px</div>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80 border-0">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm mb-1.5 block">Variable Name</Label>
                    <Input value={scale.name} onChange={(e) => handleNameChange(i, e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm mb-1.5 block">Min Size (px)</Label>
                      <Input type="number" value={scale.minSize} onChange={(e) => handleScaleValueChange(i, 'minSize', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-sm mb-1.5 block">Max Size (px)</Label>
                      <Input type="number" value={scale.maxSize} onChange={(e) => handleScaleValueChange(i, 'maxSize', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">Clamp Value</Label>
                    <div className="flex gap-2">
                      <Input value={generateClampValue(scale.minSize, scale.maxSize)} readOnly className="font-mono text-xs text-muted-foreground bg-muted/30" />
                      <Button variant="outline" size="icon" className="shrink-0" onClick={() => handleCopyClampValue(generateClampValue(scale.minSize, scale.maxSize))}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/20" onClick={() => handleRemoveSpacing(i)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ))}
          <Button variant="outline" className="w-full mt-2 border-dashed border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30" onClick={handleAddSpacing}>
            + Add New
          </Button>
        </div>
      </div>

      {/* RADIUS */}
      <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-8 shrink-0">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border/50 bg-muted/20">
          <Circle className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground uppercase">Radius</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[2, 3, 5].map((scaleIndex, idx) => {
              const scale = radiusScale[scaleIndex] || radiusScale[0];
              return (
                <div key={idx} className={`aspect-[3/2] border ${idx === 0 ? 'border-primary' : 'border-transparent'} bg-background/50 flex items-center justify-center rounded-lg p-3 transition-colors`}>
                  <div className="w-full h-full border border-border/50 bg-muted/10" style={{ borderRadius: `${scale.maxSize}px` }} />
                </div>
              );
            })}
          </div>
          
          <div className="flex flex-col gap-1">
            {radiusScale.map((scale, i) => (
              <Popover key={i}>
                <PopoverTrigger asChild>
                  <div className="flex items-center justify-between py-2.5 px-3 -mx-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors group">
                    <div className="text-sm font-medium text-foreground">{scale.name}</div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground">{scale.maxSize}px</div>
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-80 border-0">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm mb-1.5 block">Variable Name</Label>
                      <Input value={scale.name} onChange={(e) => handleRadiusNameChange(i, e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm mb-1.5 block">Min Size (px)</Label>
                        <Input type="number" value={scale.minSize} onChange={(e) => handleRadiusValueChange(i, 'minSize', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-sm mb-1.5 block">Max Size (px)</Label>
                        <Input type="number" value={scale.maxSize} onChange={(e) => handleRadiusValueChange(i, 'maxSize', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm mb-1.5 block">Clamp Value</Label>
                      <div className="flex gap-2">
                        <Input value={generateClampValue(scale.minSize, scale.maxSize)} readOnly className="font-mono text-xs text-muted-foreground bg-muted/30" />
                        <Button variant="outline" size="icon" className="shrink-0" onClick={() => handleCopyClampValue(generateClampValue(scale.minSize, scale.maxSize))}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="icon" className="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/20" onClick={() => handleRemoveRadius(i)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            ))}
            <Button variant="outline" className="w-full mt-2 border-dashed border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30" onClick={handleAddRadius}>
              + Add New
            </Button>
          </div>
        </div>
      </div>

      {/* ELEVATION */}
      <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-8 shrink-0">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border/50 bg-muted/20">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground uppercase">Elevation & Depth</h3>
        </div>
        <div className="p-6 flex flex-col gap-1">
          {elevationScale.map((scale, i) => (
            <Popover key={i}>
              <PopoverTrigger asChild>
                <div className="flex items-center justify-between py-2.5 px-3 -mx-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors group">
                  <div className="flex-1 text-sm font-medium text-foreground truncate pr-2">{scale.name}</div>
                  <div className="flex items-center justify-end">
                    <div className={`w-14 h-8 rounded-md bg-card flex-shrink-0 ${scale.className}`} />
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80 border-0">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm mb-1.5 block">Level Name</Label>
                    <Input value={scale.name} onChange={(e) => handleElevationNameChange(i, e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">Reference</Label>
                    <Input value={scale.reference || ''} onChange={(e) => handleElevationReferenceChange(i, e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">CSS Value</Label>
                    <Input value={scale.value} onChange={(e) => handleElevationValueChange(i, e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block">Tailwind Classes</Label>
                    <Input value={scale.className} onChange={(e) => handleElevationClassChange(i, e.target.value)} />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-500/20" onClick={() => handleRemoveElevation(i)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Level
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ))}
          <Button variant="outline" className="w-full mt-2 border-dashed border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/30" onClick={handleAddElevation}>
            + Add New
          </Button>
        </div>
      </div>
    </div>
  );
}
